import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { BASE_URL } from '../context/api';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../context/AuthContext';

const Loader = () => (
  <div className="flex justify-center items-center">
    <div className="relative">
      <div className="w-12 h-12 rounded-full absolute border-4 border-pink-200"></div>
      <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-pink-600 border-t-transparent"></div>
    </div>
  </div>
);

const ViewInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [searchParams, setSearchParams] = useState({
    classType: '',
    form: '',
    term: '',
    year: '',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const invoicesPerPage = 10;

  const navigate = useNavigate();
  const { username } = useContext(AuthContext); // Fetch the username from AuthContext

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const response = await axios.get(`${BASE_URL}/invoices`);
        setInvoices(response.data);
        setFilteredInvoices(response.data);
      } catch (error) {
        console.error('Failed to fetch invoices', error);
      }
    };

    fetchInvoices();
  }, []);

  const handleSearch = () => {
    const filtered = invoices.filter(invoice => 
      (searchParams.classType ? invoice.class_type.includes(searchParams.classType) : true) &&
      (searchParams.form ? invoice.form.toString() === searchParams.form : true) &&
      (searchParams.term ? invoice.term.toString() === searchParams.term : true) &&
      (searchParams.year ? invoice.year.toString() === searchParams.year : true)
    );
    setFilteredInvoices(filtered);
    setCurrentPage(1);
  };

  const handleReset = () => {
    setSearchParams({
      classType: '',
      form: '',
      term: '',
      year: '',
    });
    setFilteredInvoices(invoices);
    setCurrentPage(1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSearchParams({ ...searchParams, [name]: value });
  };

  const handleEdit = (invoiceId) => {
    if (username === 'financeadmin' || username === 'sysadmin') {
      navigate(`/edit-invoice/${invoiceId}`);
    } else {
      toast.error("You don't have permission to edit invoices.");
    }
  };

  const handleView = (invoice) => {
    setSelectedInvoice(invoice);
  };

  const closeModal = () => {
    setSelectedInvoice(null);
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${BASE_URL}/invoices/${selectedInvoice.id}`);
      toast.success("Invoice deleted successfully.");
      setInvoices(invoices.filter(invoice => invoice.id !== selectedInvoice.id));
      setFilteredInvoices(filteredInvoices.filter(invoice => invoice.id !== selectedInvoice.id));
      setShowDeleteModal(false);
      setSelectedInvoice(null);
    } catch (error) {
      console.error('Failed to delete invoice', error);
      toast.error('Failed to delete invoice.');
    }
  };

  const openDeleteModal = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setSelectedInvoice(null);
    setShowDeleteModal(false);
  };

  const indexOfLastInvoice = currentPage * invoicesPerPage;
  const indexOfFirstInvoice = indexOfLastInvoice - invoicesPerPage;
  const currentInvoices = filteredInvoices.slice(indexOfFirstInvoice, indexOfLastInvoice);

  const renderPageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredInvoices.length / invoicesPerPage); i++) {
    renderPageNumbers.push(
      <button 
        key={i} 
        onClick={() => setCurrentPage(i)} 
        className={`px-4 py-2 mx-1 rounded-md ${
          i === currentPage 
            ? 'bg-pink-600 text-white' 
            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        } focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2`}
      >
        {i}
      </button>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Invoices</h1>
            <div className="h-1 w-full bg-gray-200 mt-2"></div>
          </div>

          {/* Search Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Class Type</label>
              <input
                type="text"
                name="classType"
                placeholder="Search by class type"
                value={searchParams.classType}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Form</label>
              <input
                type="text"
                name="form"
                placeholder="Search by form"
                value={searchParams.form}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
              <input
                type="text"
                name="term"
                placeholder="Search by term"
                value={searchParams.term}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
              <input
                type="text"
                name="year"
                placeholder="Search by year"
                value={searchParams.year}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end gap-4 mb-6">
            <button 
              onClick={handleSearch} 
              className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
            >
              Search
            </button>
            <button 
              onClick={handleReset} 
              className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Reset
            </button>
          </div>

          {/* Invoice Table */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-pink-600 text-white">
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider rounded-tl-lg">Class Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Form</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Term</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Year</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider rounded-tr-lg">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentInvoices.map((invoice, index) => (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{invoice.class_type}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{invoice.form}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{invoice.term}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{invoice.year}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleEdit(invoice.id)} 
                          className="px-3 py-1 text-sm bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={!(username === 'financeadmin' || username === 'sysadmin')}
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleView(invoice)} 
                          className="px-3 py-1 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                          View
                        </button>
                        {username === 'financeadmin' || username === 'sysadmin' ? (
                          <button 
                            onClick={() => openDeleteModal(invoice)} 
                            className="px-3 py-1 text-sm bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                          >
                            Delete
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex justify-center mt-6">
            {renderPageNumbers.map((pageNum) => (
              <button 
                key={pageNum} 
                onClick={() => setCurrentPage(pageNum)} 
                className={`px-4 py-2 mx-1 rounded-md ${
                  pageNum === currentPage 
                    ? 'bg-pink-600 text-white' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2`}
              >
                {pageNum}
              </button>
            ))}
          </div>

          {/* Modal for Viewing Invoice Details */}
          {selectedInvoice && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
                <div className="p-6">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-800">Invoice Details</h3>
                    <div className="h-1 w-full bg-gray-200 mt-2"></div>
                  </div>
                  
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Invoice ID</p>
                        <p className="text-lg font-semibold text-gray-900">#{selectedInvoice.id}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Class Type</p>
                        <p className="text-lg font-semibold text-gray-900">{selectedInvoice.class_type}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Form</p>
                        <p className="text-lg font-semibold text-gray-900">{selectedInvoice.form}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Term</p>
                        <p className="text-lg font-semibold text-gray-900">{selectedInvoice.term}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Year</p>
                        <p className="text-lg font-semibold text-gray-900">{selectedInvoice.year}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">RTGS Percentage</p>
                        <p className="text-lg font-semibold text-gray-900">{selectedInvoice.rtgs_percentage}%</p>
                      </div>
                    </div>

                    <div className="mt-6">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">Items</h4>
                      <div className="space-y-2">
                        {selectedInvoice.items.map((item) => (
                          <div key={item.id} className="flex justify-between items-center py-2 border-b">
                            <span className="text-gray-700">{item.item_name}</span>
                            <span className="font-semibold text-gray-900">${item.amount}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mt-6 pt-4 border-t">
                      <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold text-gray-800">Total Amount</span>
                        <span className="text-2xl font-bold text-pink-600">${selectedInvoice.total_amount}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <button
                      onClick={closeModal}
                      className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Modal for Delete Confirmation */}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
                <div className="p-6">
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-800">Confirm Deletion</h3>
                    <div className="h-1 w-full bg-gray-200 mt-2"></div>
                  </div>
                  
                  <p className="text-gray-600 mb-6">
                    You are about to delete this invoice. If there are students who have already paid, their balances will be affected.
                  </p>

                  <div className="flex justify-end gap-4">
                    <button 
                      onClick={closeDeleteModal} 
                      className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={handleDelete} 
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewInvoices;
